import React, { useEffect, useState } from 'react';
import { getLevelShorts } from '../services/apiService';
import '../styles/WeeklyCondorPanel.css';

// Level short (LevelPutSpread) — paper put debit spreads opened when an hourly candle touches the previous
// day's / week's level, the next confirms below its low and spot breaks that low; held up to 5 sessions, outside the intraday book. Same look as the condor panel.
const POLL_MS = 30000;

const fmtRs = v => v == null ? '—' : `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;
const fmt2  = v => v == null ? '—' : Number(v).toFixed(2);
const fmtI  = v => v == null ? '—' : Math.round(v).toLocaleString('en-IN');
const day   = iso => iso ? String(iso).slice(0, 16).replace('T', ' ') : '—';
const pnlColor = v => v == null ? '#e0e0e0' : v >= 0 ? '#00c853' : '#ef5350';

const OpenSpread = ({ p }) => {
  const spot = p.lastSpot ?? p.entrySpot;
  // Where spot sits between the stop (0%) and the target (100%).
  const progress = spot && p.stop > p.target
    ? Math.max(0, Math.min(100, ((p.stop - spot) / (p.stop - p.target)) * 100)) : null;
  return (
    <div className="wc-open">
      <div className="wc-structure">
        <span>{p.structure}</span>
        <span className="wc-muted">exp {p.expiry} · rejected {p.level} {fmtI(p.levelPrice)}</span>
      </div>
      <div className="wc-grid">
        <div className="wc-item"><span className="wc-label">Debit</span><span className="wc-value">₹{fmt2(p.debit)} × {p.qty}</span></div>
        <div className="wc-item"><span className="wc-label">Spread value</span><span className="wc-value">{p.lastValue != null ? `₹${fmt2(p.lastValue)}` : 'no mark yet'}</span></div>
        <div className="wc-item">
          <span className="wc-label">Unrealised</span>
          <span className="wc-value" style={{ color: pnlColor(p.unrealisedRs) }}>{fmtRs(p.unrealisedRs)}</span>
        </div>
        <div className="wc-item"><span className="wc-label">Max profit</span><span className="wc-value">₹{fmtI((p.width - p.debit) * p.qty)}</span></div>
        <div className="wc-item">
          <span className="wc-label">Spot</span>
          <span className="wc-value">{fmtI(spot)} <span className="wc-muted">(entry {fmtI(p.entrySpot)})</span></span>
        </div>
        <div className="wc-item"><span className="wc-label">Stop / target (spot)</span><span className="wc-value">{fmtI(p.stop)} / {fmtI(p.target)}</span></div>
        <div className="wc-item"><span className="wc-label">Time exit</span><span className="wc-value">{p.timeExit} 15:15</span></div>
      </div>
      {progress != null && (
        <div className="wc-bar" title="Where spot sits between the stop (0%) and the target (100%)">
          <div className="wc-bar-fill" style={{ width: `${progress}%`, background: progress >= 50 ? '#00c853' : progress >= 20 ? '#ffb300' : '#ef5350' }} />
          <span className="wc-bar-text">{progress.toFixed(0)}% of the way from stop to target</span>
        </div>
      )}
      {p.pendingExit && <div className="wc-muted" style={{ color: '#ffb300' }}>Exit waiting for a quote: {p.pendingExit}</div>}
      <div className="wc-muted">Opened {day(p.opened)}</div>
    </div>
  );
};

const LevelShortPanel = ({ username, symbol }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    const poll = () => getLevelShorts(username)
      .then(d => { if (!cancelled) { setData(d); setError(false); } })
      .catch(() => { if (!cancelled) setError(true); }); // keep the last-known view on a transient failure
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [username]);

  if (!data) return error ? null : <div className="wc-panel wc-muted">Level short: loading…</div>;

  const sym = symbol ? String(symbol).toUpperCase() : null;
  const traded = data.symbols || [];
  if (sym && traded.length && !traded.includes(sym)) {
    return <div className="wc-panel wc-muted">Level short (paper) runs on {traded.join(', ')} only — not on {sym}.</div>;
  }
  const mine = x => !sym || x.symbol === sym;
  const open = (data.open || []).filter(mine);
  const history = (data.history || []).filter(mine);
  const wins = history.filter(h => h.outcome === 'WIN').length;
  const net = history.reduce((a, h) => a + (h.netRs || 0), 0);
  const check = sym ? (data.lastCheck || {})[sym] : null;

  return (
    <div className="wc-panel">
      <div className="wc-header">
        <span className="wc-title">Level Short — put spread <span className="wc-muted">(paper)</span></span>
        <span className="wc-badge" style={{ background: !data.enabled ? '#616161' : open.length ? '#1e88e5' : '#455a64' }}>
          {!data.enabled ? 'OFF' : open.length ? 'HOLDING' : 'FLAT'}
        </span>
      </div>
      <div className="wc-rules">{data.rules}</div>

      {open.map(p => <OpenSpread key={p.clientId} p={p} />)}
      {data.enabled && open.length === 0 && (
        <div className="wc-flat">
          No spread open.
          {check && <div className="wc-muted">Last check: {check}</div>}
        </div>
      )}

      <div className="wc-summary">
        <span>{history.length} closed · {wins}W / {history.length - wins}L</span>
        <span style={{ color: pnlColor(net) }}>net {fmtRs(net)}</span>
      </div>

      {history.length > 0 && (
        <table className="wc-table">
          <thead>
            <tr><th>Opened</th><th>Debit</th><th>Exit</th><th>Pts</th><th>Net</th><th>Exit reason</th></tr>
          </thead>
          <tbody>
            {history.map(h => (
              <tr key={`${h.symbol}-${h.opened}`} title={h.structure}>
                <td>{day(h.opened)}</td>
                <td>{fmt2(h.debit)}</td>
                <td>{fmt2(h.exitCredit)}</td>
                <td style={{ color: pnlColor(h.pnlPts) }}>{fmt2(h.pnlPts)}</td>
                <td style={{ color: pnlColor(h.netRs) }}>{fmtRs(h.netRs)}</td>
                <td className="wc-reason">{h.exitReason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="wc-guide">
        Replay with option prices (Jul 2024 – Oct 2026, 1 lot, NIFTY + SENSEX): 106 spreads, 42% won, about +₹1.30 lakh,
        max drawdown ₹16k. Entry waits for a confirmation candle and the break of its low. Separate from the intraday book.
      </div>
    </div>
  );
};

export default LevelShortPanel;
